// API
import { createBaseProject, updateProjectSpeckleStream } from "@/api/services/BuildingProject";
import { createNewBranchForStream, createStreamAndBranches, deleteSpeckleStream } from "@/api/services/Speckle";

// Utils
import * as Sentry from "@sentry/vue";
import { useToast } from "vue-toastification";

// Services
import { saveProjectDetailsStep } from "./WizardStepSaveService";
import { ProjectSetupIncompleteError } from "./projectWriteErrors";

// Providers
import {
  useModels,
  useCreateBuildingProject,
  LocalModelType,
  CreateBuildingProjectType,
  NewBuildingProjectType,
  EmissionFactorType,
} from "@/features/BuildingProject";
import useInstallationEmissionFactors from "../providers/InstallationEmissionFactorsProvider";
import { useStageDefaultEmissions } from "@/dataProviders/StageDefaultEmissions";
import { withoutIncompleteReferenceAreaEntries } from "../utils/referencedAreasPayload";
import { readProjectSpeckleStreamId } from "./projectSpeckleStreamRead";

// A row is included if the user picked a data source — a built-in `type` from the supply-type dropdown,
// OR a custom one (`customSourceId` / inline-create `customSource`).
const hasMeaningfulSelection = (row: EmissionFactorType | null | undefined): boolean => {
  if (!row) return false;
  return Boolean(row.type) || Boolean(row.customSourceId) || Boolean(row.customSource);
};

/** A write that failed AFTER the project itself was written. Shared by both setup passes below so they
 * can't describe the same situation differently; it names the settings page, where the flow lands anyway. */
const reportIncompleteSetup = (error: unknown) => {
  Sentry.captureException(error);
  useToast().error("The project was created, but some of its setup could not be saved. Check its settings.");
};

const createBuildingProject = async ({
  speckleConnectionChoice,
  speckleStreamId,
}: {
  speckleConnectionChoice: number;
  speckleStreamId?: string;
}) => {
  // Providers

  const models = useModels();
  const createBuildingProject = useCreateBuildingProject();
  const installationEmissionFactors = useInstallationEmissionFactors();

  // Logic

  // Get existing project
  const project: NewBuildingProjectType = createBuildingProject.getProject();

  // Get active models
  const activeModels: Array<LocalModelType> = models.getActiveModels();

  // Extract constructional and operational data
  const {
    emissionCalculationAssumptions: {
      projectEnergyData,
      projectEnergyData: { constructional, operational },
    },
  } = project;

  // Map NewBuildingProjectType to CreateBuildingProjectType
  const createProjectData: CreateBuildingProjectType = {
    projectName: project.projectName,
    clientName: project.clientName,
    builder: project.builder,
    buildingAddress: project.buildingAddress,
    lcaResponsibleName: project.lcaResponsibleName,
    numberOfPeople: project.numberOfPeople,
    type: Number.parseInt(project.buildingTypeEnum as unknown as string),
    stage: Number.parseInt(project.buildingProjectStageEnum as unknown as string),
    projectNumber: project.projectNumber,
    streamId: project.streamId,
    emissionMetaData: project.emissionCalculationAssumptions.emissionMetaData,
    referencedAreas: withoutIncompleteReferenceAreaEntries(project.emissionCalculationAssumptions.referencedAreas),
    calculationType: project.emissionCalculationAssumptions.calculationType,
    heatArea: project.emissionCalculationAssumptions.heatArea,
    wastagePercentage: project.emissionCalculationAssumptions.wastagePercentage,
    projectEnergyData: {
      ...projectEnergyData,
      // Note that constructional and operational are optional and should be set to undefined if they are not present (e.g. value is null)
      constructional: {
        electricity: hasMeaningfulSelection(constructional?.electricity) ? constructional?.electricity ?? null : null,
        earthMoved: hasMeaningfulSelection(constructional?.earthMoved) ? constructional?.earthMoved ?? null : null,
        heat: hasMeaningfulSelection(constructional?.heat) ? constructional?.heat ?? null : null,
        machineryFuel: hasMeaningfulSelection(constructional?.machineryFuel) ? constructional?.machineryFuel ?? null : null,
      },
      operational: {
        electricity: hasMeaningfulSelection(operational?.electricity) ? operational?.electricity ?? null : null,
        heat: hasMeaningfulSelection(operational?.heat) ? operational?.heat ?? null : null,
      },
    },
    transportTypes: project.transportTypes,
    branches: activeModels.map(({ name }) => name),

    speckleConfiguration: project.speckleConfiguration,
    specialConditionConfig: project.specialConditionConfig,
  };

  // A copy, not an assignment into the object: emissionMetaData above is the store's own object, so writing
  // through it would replace the project's numeric stage ids with labels — which Hasura's integer column rejects outright.
  createProjectData.emissionMetaData = {
    ...project.emissionCalculationAssumptions.emissionMetaData,
    includedPhases: project.emissionCalculationAssumptions.emissionMetaData.includedPhasesTranslated,
  };

  try {
    // Two calls, not one: no insert permission for user-role `project` (base endpoint mints it), and no
    // update permission for status/metadata (stay in store till one exists). Idempotent: whoever asks second finds the project already made — keeps Back safe in the dialog.
    const existingProjectId = createBuildingProject.getBuildingProjectId();
    let newProjectId = existingProjectId;

    if (!newProjectId) {
      // The start dialog cannot be completed without a building type and the column is NOT NULL, so an
      // absent one is a bug rather than a state worth papering over with a fallback.
      if (createProjectData.type === null || Number.isNaN(createProjectData.type)) {
        throw new Error("A building type is required to create a project.");
      }

      const baseProject = await createBaseProject({
        name: createProjectData.projectName,
        buildingTypeId: createProjectData.type,
        yearOfConstruction: createProjectData.emissionMetaData.yearOfConstruction,
        yearOfCommissioning: createProjectData.emissionMetaData.yearOfCommissioning,
        // The dialog's optional status field: createProjectData.stage is typed number | null, but an unset
        // field is Number.parseInt()'s NaN rather than null — both are checked here to narrow to plain number.
        projectStatusId:
          createProjectData.stage === null || Number.isNaN(createProjectData.stage) ? undefined : createProjectData.stage,
        // Every model the dialog knows about, not just the active ones — the payload records an inactive
        // default the same way the dialog shows it, toggled off.
        models: models.getModels().map(({ name, isActive }) => ({ name, active: isActive })),
      });

      newProjectId = baseProject.id;

      // Set before the setup writes below, not after: the project EXISTS once the endpoint answers 201, so
      // holding the id back until every write succeeds would make a failed write look like a failed create and let a retry mint a second project.
      createBuildingProject.setBuildingProjectId(newProjectId);
    }

    /** The backend refuses to compose a project without its location and calculation row, so a failure here throws (not toast-and-continue).
     * Success is tracked by a flag, not the id, so a retry redoes these writes; safe because the upserts key on (project_id, revision_id). */
    if (!createBuildingProject.getSetupSaved()) {
      try {
        await saveProjectDetailsStep(project, newProjectId, true);
      } catch (error) {
        Sentry.captureException(error);
        throw new ProjectSetupIncompleteError(error);
      }
      createBuildingProject.setSetupSaved(true);
    }

    /** The 3D model is attached after creation, not carried into it — the Hasura insert has no stream-id
     * column, so the project must exist first. A failure here is deliberately NOT a failed create: it's reported and re-attachable from the project's own settings. */
    let attachedStreamId = "";
    if (speckleConnectionChoice !== 0) {
      try {
        if (typeof speckleStreamId === "string" && speckleStreamId.length > 0) {
          // The dialog's second pass already created the stream; only the attach remains.
          attachedStreamId = speckleStreamId;
          await pointProjectAtStream(newProjectId, attachedStreamId, activeModels);
        } else {
          attachedStreamId = await attachNewStreamToProject(newProjectId, project.projectName, activeModels);
        }
      } catch (error) {
        attachedStreamId = "";
        Sentry.captureException(error);
        useToast().error(
          "The project was created, but the 3D model could not be connected. You can connect it from the project."
        );
      }
    }

    // Set the new stream id
    createBuildingProject.setSpeckleStreamId(attachedStreamId);

    // Only for a project written by THIS call — on the second pass (3D model step, project already
    // created earlier), these have already run and running them again would double-insert the transport rows.
    if (existingProjectId) return 200;

    // Same terms again: these write onto a project that already exists, so none of them can turn it
    // back into a failed create.
    try {
      await Promise.all([
        // Insert transport types
        project?.transportTypes?.length > 0 ? createBuildingProject.insertProjectTransport() : undefined,
        // Guarded, not just tidy: Installation is no longer a create step, so nothing has mounted its store.
        // Called unconditionally, its mutation composable can't resolve an Apollo client outside component setup and fails the whole create.
        installationEmissionFactors.items.length > 0 ? installationEmissionFactors.save(newProjectId) : undefined,
        // Persist the per-stage standard value toggles now that a project id exists
        useStageDefaultEmissions().saveEnabledStages(newProjectId),
      ]);
    } catch (error) {
      reportIncompleteSetup(error);
    }

    // Return the new project id
    return 200;
  } catch (error) {
    console.warn(error);
    return error as any;
  }
};

export default createBuildingProject;

/** The write is the API's alone — no client permission covers any of it (Marten, Hasura and the Speckle
 * webhook's stream-id lookup all have to stay in step). Read past the cache below: the attach answers 200 without visibly changing the project row, and an empty model-names list is a real answer, not an omission. */
const pointProjectAtStream = async (
  projectId: string,
  streamId: string,
  activeModels: Array<LocalModelType | string>
): Promise<void> => {
  await updateProjectSpeckleStream(projectId, streamId, activeModelNames(activeModels));

  const attached = await readProjectSpeckleStreamId(projectId);
  if (attached !== streamId) {
    throw new Error(`The API accepted the attach but the project reads speckleStreamId=${JSON.stringify(attached)}`);
  }
};

/** Exported as one function so every caller avoids the same traps: create the stream FIRST then attach it,
 * so a project never points at a nonexistent stream, and delete it again if the attach fails. */
export const attachNewStreamToProject = async (
  projectId: string,
  projectName: string,
  activeModels: Array<LocalModelType>
): Promise<string> => {
  const streamId = await createStreamForProject(projectName, activeModels);

  try {
    await pointProjectAtStream(projectId, streamId, activeModels);
  } catch (attachError) {
    // Ours and attached to nothing, so it goes. Best effort: the attach failure is the one to report.
    await deleteSpeckleStream({ streamId }).catch((deleteError: unknown) => Sentry.captureException(deleteError));
    throw attachError;
  }

  return streamId;
};

/** Attach a stream that already exists (another project's) so both read the same model — no stream is
 * created here; `activeModels` are the shared stream's model names this project includes. */
export const attachSharedStreamToProject = async (
  projectId: string,
  streamId: string,
  activeModels: Array<string>
): Promise<string> => {
  await pointProjectAtStream(projectId, streamId, activeModels);
  return streamId;
};

// Lower-cased like getAddedBranches and ModelsProvider.addModel name branches, and like Speckle stores them.
const activeModelNames = (activeModels: Array<LocalModelType | string>): Array<string> =>
  activeModels.map((model) => (typeof model === "string" ? model : model.name).toLowerCase());

/** A new Speckle stream with a branch per model the flow selected — the type's own defaults in one call,
 * then any the user added by hand. Returns the stream id. */
const createStreamForProject = async (projectName: string, activeModels: Array<LocalModelType>): Promise<string> => {
  const streamId = await createStreamAndBranches(projectName, getDefaultBranches(activeModels));

  // The endpoint answers with the status code when it did not create one, so anything that is not an id
  // is a failure — and attaching it would point the project at a stream that does not exist.
  if (typeof streamId !== "string" || streamId.length === 0) {
    throw new Error(`Could not create a Speckle stream (${String(streamId)})`);
  }

  const addedBranches = getAddedBranches(activeModels, streamId);
  if (addedBranches.length > 0) {
    await createNewBranchForStream({ streamId, branches: addedBranches });
  }

  return streamId;
};

const getDefaultBranches = (models: Array<LocalModelType>) => {
  const defaultBranches = models.filter((localModel) => localModel.enumValue !== undefined);

  const result: Array<number> = [];

  defaultBranches.forEach((branch) => {
    if (branch.enumValue !== undefined) result.push(branch.enumValue);
  });

  return result;
};

const getAddedBranches = (models: Array<LocalModelType>, streamId: string) => {
  const addedBranches = models.filter((localModel) => localModel.enumValue === undefined);

  const result: Array<{ name: string; description: string }> = [];
  addedBranches.forEach((branch) => {
    result.push({
      name: branch.name.toLowerCase(),
      description: `Branch for stream - ${branch.name} with id - ${streamId}`,
    });
  });

  return result;
};
